import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Card,
  CardBody,
  VStack,
  HStack,
  Button,
  useToast,
  Spinner,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  SimpleGrid,
} from '@chakra-ui/react';
import { FiFolder, FiFile, FiRefreshCw, FiHome, FiEye, FiUpload, FiDownload } from 'react-icons/fi';
import DataTable from 'react-data-table-component';
import { hospitalityApi } from '../config/api';

const IntegrationData = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const pathFromUrl = searchParams.get('path') || '';
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ path: '/', folders: [], files: [] });
  const [error, setError] = useState(null);
  const toast = useToast();

  const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure();
  const [previewFilePath, setPreviewFilePath] = useState('');
  const [previewData, setPreviewData] = useState({ filePath: '', sheets: [] });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);

  const [dsrData, setDsrData] = useState([]);
  const [dsrTotal, setDsrTotal] = useState(0);
  const [dsrLoading, setDsrLoading] = useState(false);
  const [dsrFilters, setDsrFilters] = useState({ dateFrom: '', dateTo: '', search: '' });

  const [statData, setStatData] = useState([]);
  const [statTotal, setStatTotal] = useState(0);
  const [statLoading, setStatLoading] = useState(false);
  const [statFilters, setStatFilters] = useState({ dateFrom: '', dateTo: '', search: '' });

  const fetchList = useCallback(
    async (path = '') => {
      setLoading(true);
      setError(null);
      try {
        const response = await hospitalityApi.get('/integrationData', {
          params: path ? { path } : {},
        });
        if (response.data.status === 200) {
          setData(response.data.data || { path: '/', folders: [], files: [] });
        } else {
          setError(response.data.message || 'Gagal mengambil data');
        }
      } catch (err) {
        const msg = err.response?.data?.message || err.message || 'Gagal mengambil data FTP';
        setError(msg);
        toast({
          title: 'Error',
          description: msg,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    },
    [toast]
  );

  useEffect(() => {
    fetchList(pathFromUrl);
  }, [pathFromUrl, fetchList]);

  const navigateTo = (path) => {
    setSearchParams(path ? { path } : {});
  };

  const breadcrumbParts = (data.path || '/').replace(/\/+/g, '/').split('/').filter(Boolean);
  const currentPath = data.path || '/';

  const goToParent = () => {
    if (currentPath === '/' || !currentPath) return;
    const parts = currentPath.replace(/\/+/g, '/').split('/').filter(Boolean);
    parts.pop();
    navigateTo(parts.length ? '/' + parts.join('/') : '');
  };

  const formatSize = (size) => {
    if (size === '-' || size == null) return '-';
    const n = Number(size);
    if (isNaN(n)) return String(size);
    if (n < 1024) return n + ' B';
    if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
    return (n / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const isXlsx = (name) => (name || '').toLowerCase().endsWith('.xlsx');
  const isCsv = (name) => (name || '').toLowerCase().endsWith('.csv');
  const isPreviewableFile = (name) => isXlsx(name) || isCsv(name);

  const getFilePath = (file) => (currentPath === '/' ? '/' + file.name : currentPath + '/' + file.name);

  const handleFileClick = (file) => {
    if (!isPreviewableFile(file.name)) return;
    const filePath = getFilePath(file);
    setPreviewFilePath(filePath);
    setPreviewData({ filePath: '', sheets: [] });
    onPreviewOpen();
    setPreviewLoading(true);
    hospitalityApi
      .get('/integrationData/preview', { params: { path: filePath } })
      .then((res) => {
        if (res.data.status === 200) {
          setPreviewData(res.data.data || { filePath, sheets: [] });
        } else {
          toast({ title: 'Error', description: res.data.message || 'Gagal preview', status: 'error', isClosable: true });
        }
      })
      .catch((err) => {
        const msg = err.response?.data?.message || err.message || 'Gagal preview file';
        toast({ title: 'Error', description: msg, status: 'error', isClosable: true });
      })
      .finally(() => setPreviewLoading(false));
  };

  const handleDownload = async (file, e) => {
    e?.stopPropagation();
    const filePath = getFilePath(file);
    try {
      const res = await hospitalityApi.get('/integrationData/download', {
        params: { path: filePath },
        responseType: 'blob',
      });
      if (res.status !== 200 || !(res.data instanceof Blob)) {
        toast({ title: 'Error', description: 'Gagal download file', status: 'error', isClosable: true });
        return;
      }
      const blob = res.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name || 'download';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast({ title: 'Download berhasil', description: file.name, status: 'success', duration: 2000, isClosable: true });
    } catch (err) {
      let msg = err.message || 'Gagal download file';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          msg = json.message || msg;
        } catch (_) {}
      } else if (err.response?.data?.message) {
        msg = err.response.data.message;
      }
      toast({ title: 'Error', description: msg, status: 'error', isClosable: true });
    }
  };

  const fetchDsrList = useCallback(async () => {
    setDsrLoading(true);
    try {
      const params = { limit: 200 };
      if (dsrFilters.dateFrom) params.dateFrom = dsrFilters.dateFrom;
      if (dsrFilters.dateTo) params.dateTo = dsrFilters.dateTo;
      if (dsrFilters.search?.trim()) params.search = dsrFilters.search.trim();
      const res = await hospitalityApi.get('/integrationData/dsr', { params });
      if (res.data.status === 200) {
        setDsrData(res.data.data || []);
        setDsrTotal(res.data.total ?? 0);
      }
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.message || err.message || 'Gagal mengambil data DSR', status: 'error', isClosable: true });
      setDsrData([]);
      setDsrTotal(0);
    } finally {
      setDsrLoading(false);
    }
  }, [dsrFilters.dateFrom, dsrFilters.dateTo, dsrFilters.search, toast]);

  const fetchStatisticList = useCallback(async () => {
    setStatLoading(true);
    try {
      const params = { limit: 200 };
      if (statFilters.dateFrom) params.dateFrom = statFilters.dateFrom;
      if (statFilters.dateTo) params.dateTo = statFilters.dateTo;
      if (statFilters.search?.trim()) params.search = statFilters.search.trim();
      const res = await hospitalityApi.get('/integrationData/statistic', { params });
      if (res.data.status === 200) {
        setStatData(res.data.data || []);
        setStatTotal(res.data.total ?? 0);
      }
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.message || err.message || 'Gagal mengambil data Statistic', status: 'error', isClosable: true });
      setStatData([]);
      setStatTotal(0);
    } finally {
      setStatLoading(false);
    }
  }, [statFilters.dateFrom, statFilters.dateTo, statFilters.search, toast]);

  useEffect(() => {
    fetchDsrList();
  }, []);
  useEffect(() => {
    fetchStatisticList();
  }, []);

  const dsrColumns = useMemo(
    () => [
      { name: 'Hotel Code', selector: (row) => row.hotel_code ?? '-', sortable: true, width: '100px' },
      { name: 'Hotel Name', selector: (row) => row.hotel_name ?? '-', sortable: true, wrap: true },
      { name: 'Fo Date', selector: (row) => (row.fo_date != null ? String(row.fo_date).slice(0, 10) : '-'), sortable: true, width: '110px' },
      { name: 'List Index', selector: (row) => row.list_index ?? '-', sortable: true, width: '95px' },
      { name: 'DSR ID', selector: (row) => row.dsr_id ?? '-', sortable: true, width: '80px' },
      { name: 'Description', selector: (row) => row.description ?? '-', sortable: true, wrap: true },
      { name: 'Amount', selector: (row) => (row.amount != null ? Number(row.amount).toLocaleString('id-ID') : '-'), sortable: true, right: true, width: '120px' },
      { name: 'Today Value', selector: (row) => (row.today_value != null ? Number(row.today_value).toLocaleString('id-ID') : '-'), sortable: true, right: true, width: '120px' },
      { name: 'MTD', selector: (row) => (row.mtd != null ? Number(row.mtd).toLocaleString('id-ID') : '-'), sortable: true, right: true, width: '120px' },
      { name: 'YTD', selector: (row) => (row.ytd != null ? Number(row.ytd).toLocaleString('id-ID') : '-'), sortable: true, right: true, width: '120px' },
      { name: 'Command Type', selector: (row) => row.command_type ?? '-', sortable: true, width: '110px' },
    ],
    []
  );

  const statColumns = useMemo(
    () => [
      { name: 'Hotel Code', selector: (row) => row.hotel_code ?? '-', sortable: true, width: '100px' },
      { name: 'Hotel Name', selector: (row) => row.hotel_name ?? '-', sortable: true, wrap: true },
      { name: 'Stat Date', selector: (row) => (row.stat_date != null ? String(row.stat_date).slice(0, 10) : '-'), sortable: true, width: '110px' },
      { name: 'List Index', selector: (row) => row.list_index ?? '-', sortable: true, width: '95px' },
      { name: 'Description', selector: (row) => row.description ?? '-', sortable: true, wrap: true },
      { name: 'Today Value', selector: (row) => row.today_value ?? '-', sortable: true, width: '100px' },
      { name: 'MTD', selector: (row) => row.mtd ?? '-', sortable: true, width: '90px' },
      { name: 'YTD', selector: (row) => row.ytd ?? '-', sortable: true, width: '90px' },
      { name: 'Info Code', selector: (row) => row.info_code ?? '-', sortable: true, width: '110px' },
      { name: 'Bold Flag', selector: (row) => row.bold_flag ?? '-', sortable: true, width: '90px' },
    ],
    []
  );

  const dataTableStyles = useMemo(
    () => ({
      headRow: { style: { backgroundColor: '#f7fafc', borderBottom: '1px solid #e2e8f0', fontWeight: '600' } },
      headCells: { style: { fontSize: '13px', paddingLeft: '12px', paddingRight: '12px' } },
      cells: { style: { fontSize: '13px', paddingLeft: '12px', paddingRight: '12px' } },
      rows: { style: { minHeight: '44px', '&:not(:last-of-type)': { borderBottom: '1px solid #e2e8f0' } } },
    }),
    []
  );

  const paginationOptions = useMemo(
    () => ({
      rowsPerPageText: 'Baris per halaman:',
      rangeSeparatorText: 'dari',
      selectAllRowsItem: true,
      selectAllRowsItemText: 'Semua',
    }),
    []
  );

  const handleImport = () => {
    if (!previewFilePath) return;
    setImportLoading(true);
    hospitalityApi
      .post('/integrationData/import', { path: previewFilePath })
      .then((res) => {
        if (res.data.status === 200) {
          const d = res.data.data || {};
          toast({
            title: 'Import berhasil',
            description: res.data.message || `DSR: ${d.insertedDsr} baris, STATISTIC: ${d.insertedStatistic} baris`,
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
          onPreviewClose();
          fetchDsrList();
          fetchStatisticList();
        } else {
          toast({ title: 'Error', description: res.data.message || 'Gagal import', status: 'error', isClosable: true });
        }
      })
      .catch((err) => {
        const msg = err.response?.data?.message || err.message || 'Gagal import ke database';
        toast({ title: 'Error', description: msg, status: 'error', isClosable: true });
      })
      .finally(() => setImportLoading(false));
  };

  return (
    <Box>
      <VStack align="stretch" spacing={4} mb={6}>
        <Heading size={{ base: 'md', lg: 'lg' }} color="gray.700">
          Integration Data (FTP InJourney)
        </Heading>
        <Text fontSize="sm" color="gray.500">
          Daftar folder dan file dari FTP server. Klik folder untuk masuk. Klik file .xlsx atau .csv untuk preview. Import ke database hanya untuk .xlsx.
        </Text>
      </VStack>

      <Card bg="white" borderRadius="16px" boxShadow="sm" maxH="calc(100vh - 280px)" display="flex" flexDirection="column" overflow="hidden">
        <CardBody p={{ base: 4, lg: 6 }} flex="1" display="flex" flexDirection="column" minH={0}>
          <HStack mb={4} spacing={3} flexWrap="wrap" flexShrink={0}>
            <Button
              leftIcon={<FiRefreshCw />}
              onClick={() => fetchList(pathFromUrl)}
              isLoading={loading}
              variant="outline"
              size="sm"
            >
              Refresh
            </Button>
            <Breadcrumb spacing={2} flexWrap="wrap">
              <BreadcrumbItem>
                <BreadcrumbLink onClick={() => navigateTo('')} cursor="pointer">
                  <HStack spacing={1}>
                    <Icon as={FiHome} />
                    <Text>Root</Text>
                  </HStack>
                </BreadcrumbLink>
              </BreadcrumbItem>
              {breadcrumbParts.map((part, index) => {
                const pathUpToHere = '/' + breadcrumbParts.slice(0, index + 1).join('/');
                return (
                  <BreadcrumbItem key={pathUpToHere}>
                    <BreadcrumbLink onClick={() => navigateTo(pathUpToHere)} cursor="pointer">
                      {part}
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                );
              })}
            </Breadcrumb>
            {currentPath !== '/' && currentPath && (
              <Button size="sm" variant="ghost" onClick={goToParent}>
                ↑ Parent
              </Button>
            )}
          </HStack>

          {loading ? (
            <VStack py={12}>
              <Spinner size="lg" />
              <Text color="gray.500">Memuat daftar...</Text>
            </VStack>
          ) : error ? (
            <Text color="red.500" py={6}>
              {error}
            </Text>
          ) : (
            <>
              {data.folders.length === 0 && data.files.length === 0 ? (
                <Text color="gray.500" py={6} flexShrink={0}>
                  Folder ini kosong.
                </Text>
              ) : (
                <Box flex="1" minH={0} overflowY="auto" overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Nama</Th>
                        <Th>Ukuran</Th>
                        <Th w="80px">Tipe</Th>
                        <Th w="80px">Aksi</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {data.folders.map((folder) => {
                        const nextPath = currentPath === '/' ? '/' + folder.name : currentPath + '/' + folder.name;
                        return (
                          <Tr
                            key={folder.name}
                            _hover={{ bg: 'gray.50' }}
                            cursor="pointer"
                            onClick={() => navigateTo(nextPath)}
                          >
                            <Td>
                              <HStack>
                                <Icon as={FiFolder} color="yellow.500" />
                                <Text fontWeight="500">{folder.name}</Text>
                              </HStack>
                            </Td>
                            <Td>-</Td>
                            <Td>
                              <Badge colorScheme="yellow">Folder</Badge>
                            </Td>
                            <Td>-</Td>
                          </Tr>
                        );
                      })}
                      {data.files.map((file) => (
                        <Tr key={file.name} _hover={{ bg: 'gray.50' }} cursor={isPreviewableFile(file.name) ? 'pointer' : 'default'} onClick={() => isPreviewableFile(file.name) && handleFileClick(file)}>
                          <Td>
                            <HStack>
                              <Icon as={FiFile} color="gray.500" />
                              <Text>{file.name}</Text>
                            </HStack>
                          </Td>
                          <Td>{formatSize(file.size)}</Td>
                          <Td>
                            <Badge colorScheme="gray">File</Badge>
                          </Td>
                          <Td>
                            <HStack spacing={2}>
                              {isPreviewableFile(file.name) && (
                                <Button size="xs" leftIcon={<FiEye />} variant="outline" colorScheme="blue" onClick={(e) => { e.stopPropagation(); handleFileClick(file); }}>
                                  Preview
                                </Button>
                              )}
                              <Button size="xs" leftIcon={<FiDownload />} variant="outline" colorScheme="teal" onClick={(e) => handleDownload(file, e)}>
                                Download
                              </Button>
                            </HStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </>
          )}
        </CardBody>
      </Card>

      <Card bg="white" borderRadius="16px" boxShadow="sm" mt={6}>
        <CardBody p={{ base: 4, lg: 6 }}>
          <Heading size="sm" color="gray.700" mb={4}>
            Data Tersimpan
          </Heading>
          <Tabs variant="enclosed" colorScheme="blue">
            <TabList>
              <Tab>Daily Sales Report</Tab>
              <Tab>Statistic</Tab>
            </TabList>
            <TabPanels>
              <TabPanel p={0} pt={4}>
                <VStack align="stretch" spacing={4}>
                  <SimpleGrid columns={{ base: 1, md: 4 }} spacing={3}>
                    <FormControl>
                      <FormLabel fontSize="sm">Tanggal Dari</FormLabel>
                      <Input type="date" size="sm" value={dsrFilters.dateFrom} onChange={(e) => setDsrFilters((f) => ({ ...f, dateFrom: e.target.value }))} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Tanggal Sampai</FormLabel>
                      <Input type="date" size="sm" value={dsrFilters.dateTo} onChange={(e) => setDsrFilters((f) => ({ ...f, dateTo: e.target.value }))} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Cari (description)</FormLabel>
                      <Input placeholder="Cari..." size="sm" value={dsrFilters.search} onChange={(e) => setDsrFilters((f) => ({ ...f, search: e.target.value }))} />
                    </FormControl>
                    <FormControl display="flex" flexDir="column" justifyContent="flex-end">
                      <Button size="sm" colorScheme="blue" leftIcon={<FiRefreshCw />} onClick={fetchDsrList} isLoading={dsrLoading}>
                        Terapkan Filter
                      </Button>
                    </FormControl>
                  </SimpleGrid>
                  <Text fontSize="xs" color="gray.500" mb={2}>Total: {dsrTotal} baris</Text>
                  <DataTable
                    columns={dsrColumns}
                    data={dsrData}
                    progressPending={dsrLoading}
                    progressComponent={<VStack py={8}><Spinner /><Text color="gray.500">Memuat...</Text></VStack>}
                    noDataComponent={<Text py={6} color="gray.500">Tidak ada data. Gunakan filter atau import dari file xlsx.</Text>}
                    pagination
                    paginationPerPage={20}
                    paginationRowsPerPageOptions={[10, 20, 50, 100]}
                    paginationComponentOptions={paginationOptions}
                    customStyles={dataTableStyles}
                    highlightOnHover
                    striped
                    dense
                    responsive
                  />
                </VStack>
              </TabPanel>
              <TabPanel p={0} pt={4}>
                <VStack align="stretch" spacing={4}>
                  <SimpleGrid columns={{ base: 1, md: 4 }} spacing={3}>
                    <FormControl>
                      <FormLabel fontSize="sm">Tanggal Dari</FormLabel>
                      <Input type="date" size="sm" value={statFilters.dateFrom} onChange={(e) => setStatFilters((f) => ({ ...f, dateFrom: e.target.value }))} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Tanggal Sampai</FormLabel>
                      <Input type="date" size="sm" value={statFilters.dateTo} onChange={(e) => setStatFilters((f) => ({ ...f, dateTo: e.target.value }))} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Cari (description / info_code)</FormLabel>
                      <Input placeholder="Cari..." size="sm" value={statFilters.search} onChange={(e) => setStatFilters((f) => ({ ...f, search: e.target.value }))} />
                    </FormControl>
                    <FormControl display="flex" flexDir="column" justifyContent="flex-end">
                      <Button size="sm" colorScheme="blue" leftIcon={<FiRefreshCw />} onClick={fetchStatisticList} isLoading={statLoading}>
                        Terapkan Filter
                      </Button>
                    </FormControl>
                  </SimpleGrid>
                  <Text fontSize="xs" color="gray.500" mb={2}>Total: {statTotal} baris</Text>
                  <DataTable
                    columns={statColumns}
                    data={statData}
                    progressPending={statLoading}
                    progressComponent={<VStack py={8}><Spinner /><Text color="gray.500">Memuat...</Text></VStack>}
                    noDataComponent={<Text py={6} color="gray.500">Tidak ada data. Gunakan filter atau import dari file xlsx.</Text>}
                    pagination
                    paginationPerPage={20}
                    paginationRowsPerPageOptions={[10, 20, 50, 100]}
                    paginationComponentOptions={paginationOptions}
                    customStyles={dataTableStyles}
                    highlightOnHover
                    striped
                    dense
                    responsive
                  />
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </CardBody>
      </Card>

      <Modal isOpen={isPreviewOpen} onClose={onPreviewClose} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader>
            Preview: {previewFilePath ? previewFilePath.split('/').pop() : ''}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto" pb={6}>
            {previewLoading ? (
              <VStack py={12}>
                <Spinner size="lg" />
                <Text color="gray.500">Memuat preview...</Text>
              </VStack>
            ) : previewData.sheets && previewData.sheets.length > 0 ? (
              <Tabs variant="enclosed" colorScheme="blue">
                <TabList flexWrap="wrap">
                  {previewData.sheets.map((sheet) => (
                    <Tab key={sheet.name}>{sheet.name}</Tab>
                  ))}
                </TabList>
                <TabPanels>
                  {previewData.sheets.map((sheet) => {
                    const rows = sheet.rows || [];
                    const headerRow = rows[0] || [];
                    const dataRows = rows.slice(1);
                    return (
                      <TabPanel key={sheet.name} p={0} pt={4}>
                        <Box overflowX="auto" maxH="50vh" overflowY="auto">
                          <Table size="sm" variant="simple" whiteSpace="nowrap">
                            <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                              <Tr>
                                {headerRow.map((cell, i) => (
                                  <Th key={i} borderWidth={1}>{String(cell ?? '')}</Th>
                                ))}
                              </Tr>
                            </Thead>
                            <Tbody>
                              {dataRows.map((row, ri) => (
                                <Tr key={ri}>
                                  {(row || []).map((cell, ci) => (
                                    <Td key={ci} borderWidth={1}>{String(cell ?? '')}</Td>
                                  ))}
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                        </Box>
                        <Text fontSize="xs" color="gray.500" mt={2}>
                          {dataRows.length} baris (preview max 100 baris)
                        </Text>
                      </TabPanel>
                    );
                  })}
                </TabPanels>
              </Tabs>
            ) : (
              <Text color="gray.500">Tidak ada sheet atau file kosong.</Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onPreviewClose}>
              Tutup
            </Button>
            {previewFilePath && previewFilePath.toLowerCase().endsWith('.xlsx') && (
              <Button
                leftIcon={<FiUpload />}
                colorScheme="green"
                onClick={handleImport}
                isLoading={importLoading}
                isDisabled={previewLoading || !previewFilePath || !(previewData.sheets && previewData.sheets.length > 0)}
              >
                Import ke Database
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default IntegrationData;
